import React, { useState, useEffect, useMemo } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import "../Students/ClassScheduleCalnder.css";
import { useAuth } from "../../AuthContext.js";
import configData from "../../configure";
import AddScheduleModal from "./AddScheduleModal";

const Calendar = ({ classId, assignedCourses, orgId }) => {
  const { axios, token, userRole } = useAuth();
  const [classData, setClassData] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week");

  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // Memoize assignedCourses to prevent unnecessary re-renders
  const memoizedAssignedCourses = useMemo(() => assignedCourses, [assignedCourses]);

  // Memoize classInfo to prevent new object creation on each render
  const memoizedClassInfo = useMemo(() => ({ ...classData }), [classData]);

  useEffect(() => {
    console.log("Rendering Calendar with classId:", classId, "assignedCourses:", memoizedAssignedCourses);
    if (!classId) return;
    const fetchData = async () => {
      try {
        const response = await axios.get(`${configData.GET_CLASS_SCHEDULE_BY_ID}/${classId}`);
        setClassData(response.data);
      } catch (error) {
        console.error("Error fetching class data:", error);
        setClassData(null);
      }
    };
    fetchData();
  }, [classId, axios]);

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "week") newDate.setDate(newDate.getDate() - 7);
    else newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === "week") newDate.setDate(newDate.getDate() + 7);
    else newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleViewChange = (event, newValue) => {
    setViewMode(newValue);
  };

  const handleEditSchedule = () => {
    setIsEditMode(true);
    setIsScheduleModalOpen(true);
  };

  const renderDaySchedule = (day) => {
    if (
      !classData ||
      !classData.classScheduleMap ||
      Object.keys(classData.classScheduleMap).length === 0
    ) {
      return <Typography className="no-schedule-text">No schedule available</Typography>;
    }

    const dayName = weekdays[day.getDay()];
    const daySchedule = classData.classScheduleMap[dayName] || [];

    if (daySchedule.length === 0) {
      return <Typography className="no-schedule-text">No schedule for this day</Typography>;
    }

    return (
      <Box className="day-schedule-container">
        {Array.from({ length: 9 }, (_, i) => (
          <Box key={i} className="hour-row">
            <Typography className="hour-label">{`${8 + i}:00`}</Typography>
            <Box className="schedule-items">
              {daySchedule
                .filter(
                  (schedule) =>
                    parseInt(schedule.startTime?.split(":")[0], 10) === 8 + i
                )
                .map((schedule, index) => (
                  <Box
                    key={index}
                    className="schedule-item"
                    onClick={() => setSelectedSchedule(schedule)}
                  >
                    <Typography className="schedule-item-text">{schedule.courseName}</Typography>
                    <Typography className="schedule-item-time">
                      {schedule.startTime} - {schedule.durationMins}min
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderWeekView = () => {
    if (
      !classData ||
      !classData.classScheduleMap ||
      Object.keys(classData.classScheduleMap).length === 0
    ) {
      return <Typography className="no-schedule-text">No schedule available for this week</Typography>;
    }

    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const days = [];

    const timeColumn = (
      <Box className="time-column">
        <Box className="time-column-header">
          <Typography className="time-header-text">Time</Typography>
        </Box>
        {Array.from({ length: 9 }, (_, i) => (
          <Box key={i} className="hour-row">
            <Typography className="hour-label">{`${8 + i}:00`}</Typography>
          </Box>
        ))}
      </Box>
    );

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      const dayName = weekdays[day.getDay()];
      const daySchedule = classData.classScheduleMap[dayName] || [];

      days.push(
        <Box key={i} className="calendar-week-day">
          <Box className="day-header">
            <Typography className="day-name">{weekdays[day.getDay()]}</Typography>
            <Typography className="day-date">{day.getDate()}</Typography>
          </Box>
          <Box className="day-schedule-container">
            {Array.from({ length: 9 }, (_, i) => (
              <Box key={i} className="hour-row">
                <Box className="schedule-items">
                  {daySchedule
                    .filter(
                      (schedule) =>
                        parseInt(schedule.startTime?.split(":")[0], 10) === 8 + i
                    )
                    .map((schedule, index) => (
                      <Box
                        key={index}
                        className="schedule-item"
                        onClick={() => setSelectedSchedule(schedule)}
                      >
                        <Typography className="schedule-item-text">{schedule.courseName}</Typography>
                        <Typography className="schedule-item-time">
                          {schedule.startTime} - {schedule.durationMins}min
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box className="calendar-week-view">
        {timeColumn}
        <Box className="week-days">{days}</Box>
      </Box>
    );
  };

  const renderDayView = () => {
    const day = currentDate;
    return (
      <Box className="calendar-day-view">
        <Typography className="day-view-title">
          {`${weekdays[day.getDay()]}, ${day.getDate()} ${
            monthNames[day.getMonth()]
          } ${day.getFullYear()}`}
        </Typography>
        {renderDaySchedule(day)}
      </Box>
    );
  };

  const handleSaveSchedule = async (updatedClassData) => {
    try {
      const response = await axios.put(
        `${configData.GET_CLASS_SCHEDULE_BY_ID}/${updatedClassData.classId}`,
        updatedClassData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Schedule saved successfully!");
        setIsScheduleModalOpen(false);
        setIsEditMode(false);
        setClassData(response.data);
      }
    } catch (error) {
      console.error("Failed to save class schedule", error);
      toast.error("Error saving schedule");
    }
  };

  return (
    <Box className="calendar-wrapper">
      <Box className="calendar-container">
        <Box className="calendar-header">
          <Box className="calendar-navigation">
            <Typography className="calendar-title">
              {`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
            </Typography>
          </Box>
          
          {classData && classData.classScheduleMap && userRole !== 'STUDENT' && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditSchedule}
              className="edit-schedule-button"
            >
              Edit Schedule
            </Button>
          )}
        </Box>

        <Box className="calendar-tabs">
          <Tabs value={viewMode} onChange={handleViewChange} className="view-tabs">
            <Tab label="Day" value="day" />
            <Tab label="Week" value="week" />
          </Tabs>
        </Box>

        <Box className="calendar-content">
          {classData?.classScheduleMap === null && userRole !== 'STUDENT' && (
            <Box className="add-schedule-container">
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsScheduleModalOpen(true)}
                className="add-schedule-button"
              >
                + Add Class Schedule
              </Button>
            </Box>
          )}

          {classId ? (
            <>
              {viewMode === "day" && renderDayView()}
              {viewMode === "week" && renderWeekView()}
            </>
          ) : (
            <Typography className="no-class-text">Please select a class to view the schedule</Typography>
          )}
        </Box>
      </Box>

      {selectedSchedule && (
        <Box className="schedule-details">
          <Typography className="schedule-details-title">
            Class Details
          </Typography>
          <Box className="schedule-detail-item">
            <Typography className="detail-label">Course Name:</Typography>
            <Typography className="detail-value">{selectedSchedule.courseName}</Typography>
          </Box>
          <Box className="schedule-detail-item">
            <Typography className="detail-label">Start Time:</Typography>
            <Typography className="detail-value">{selectedSchedule.startTime}</Typography>
          </Box>
          <Box className="schedule-detail-item">
            <Typography className="detail-label">Duration:</Typography>
            <Typography className="detail-value">{selectedSchedule.durationMins} Min</Typography>
          </Box>
          <Box className="schedule-detail-item">
            <Typography className="detail-label">Faculty:</Typography>
            <Typography className="detail-value">{selectedSchedule.facultyId}</Typography>
          </Box>
          <Box className="schedule-detail-item">
            <Typography className="detail-label">Location:</Typography>
            <Typography className="detail-value">{selectedSchedule.location}</Typography>
          </Box>
        </Box>
      )}

      <AddScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => {
          setIsScheduleModalOpen(false);
          setIsEditMode(false);
        }}
        onSave={handleSaveSchedule}
        classInfo={memoizedClassInfo}
        isEditMode={isEditMode}
        assignedCourses={memoizedAssignedCourses}
        orgId={orgId}
      />

    </Box>
  );
};

export default Calendar;